/**
 * API для административных операций (AdminOffice)
 */

import { baseURL, forceDownload } from './Environment';
import { clientCC } from '../services/HttpService';
import type {
  AdminClient,
  Deal,
  ClientData,
  ClientDataResponse,
  ClientSearchParams,
  PaymentParams,
  DirectReceiptParams,
  DocumentGenerationParams,
  DocumentType,
  RateFilter,
  FileUploadResponse,
} from '../types';

// ============================================
// ОБЩЕЕ
// ============================================

/**
 * Получение списка клиентов по параметрам поиска
 */
export const fetchClients = async (clientData: ClientSearchParams): Promise<AdminClient[]> => {
  const response = await clientCC.post(`${baseURL}/admin/fetch-clients`, {
    lastName: clientData.lastname,
    firstName: clientData.firstname,
    middleName: clientData.middlename,
    series: clientData.series,
    number: clientData.number,
  });
  return response.data.content;
};

/**
 * Получение списка сделок по id клиента
 */
export const fetchDeals = async (clientId: number): Promise<Deal[]> => {
  const response = await clientCC.get(`${baseURL}/admin/fetch-credit-contracts/${clientId}`);
  return response.data.content;
};

/**
 * Получение данных клиента по id
 */
export const fetchClientData = async (clientId: number): Promise<ClientData> => {
  const response = await clientCC.get(`${baseURL}/admin/fetch-client-data/${clientId}`);
  return response.data;
};

// ============================================
// ВЫДАЧА/ЗАКРЫТИЕ
// ============================================

/**
 * Реанимация заявки по ID
 */
export const fetchReviveApplication = async (applicationId: number): Promise<boolean> => {
  const response = await clientCC.post(`${baseURL}/admin/application/${applicationId}/revive`);
  return response.status === 200;
};

/**
 * Установка точки продаж для заявки
 */
export const updateApplicationPointOfSaleAdm = async (
  creditContractId: number,
  pointOfSaleId: number
): Promise<boolean> => {
  const response = await clientCC.post(
    `${baseURL}/admin/point-of-sale?creditContractId=${creditContractId}&pointOfSaleId=${pointOfSaleId}`,
    {}
  );
  return response.status === 200;
};

/**
 * Скачивание пакета документов сделки
 */
export const fetchDealDocumentsByContractId = async (id: number): Promise<boolean> => {
  const response = await clientCC.post(
    `${baseURL}/admin/deal-documents?creditContractId=${id}`,
    {},
    { responseType: 'blob' }
  );
  forceDownload([response.data], 'deal_documents.zip');
  return response.status === 200;
};

/**
 * Установить сделку в статус "Выдан" и отправить файлы в 1С
 */
export const fetchSetDealIssued = async (creditContractId: number): Promise<void> => {
  await clientCC.post(`${baseURL}/admin/deal-issued/${creditContractId}`);
};

/**
 * Отправка запроса в ИБСО на снятие авторизации
 */
export const fetchRevokeAccount = async (creditContractId: number): Promise<void> => {
  await clientCC.post(`${baseURL}/admin/revoke-account/${creditContractId}`);
};

/**
 * Отправка запроса в ИБСО на снятие авторизации по заявке
 */
export const fetchRevokeAccountApplication = async (applicationId: number): Promise<void> => {
  await clientCC.post(`${baseURL}/admin/revoke-account-application/${applicationId}`);
};

/**
 * Отправка запроса в ИБСО на снятие авторизации по заявкам из приложенного файла
 */
export const fetchRevokeAccounts = async (formData: FormData): Promise<string> => {
  const response = await clientCC.post(`${baseURL}/admin/revoke-accounts`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

/**
 * Отправка отказа в CRIF
 */
export const fetchRejectCrifApplication = async (applicationId: number): Promise<boolean> => {
  const response = await clientCC.post(`${baseURL}/admin/application/${applicationId}/crif-reject`);
  return response.status === 200;
};

/**
 * Деактивация по 9-ФЗ
 */
export const fetchDeactivate9FZApplication = async (applicationId: number): Promise<boolean> => {
  const response = await clientCC.post(`${baseURL}/admin/application/${applicationId}/deactivate-9fz`);
  return response.status === 200;
};

/**
 * Отправка документов в ИБСО
 */
export const sendDocumentsToIbso = async (applicationId: number): Promise<string> => {
  const response = await clientCC.post(`${baseURL}/admin/send-documents-to-ibso/${applicationId}/`);
  return response.data;
};

// ============================================
// ПОГАШЕНИЯ
// ============================================

/**
 * Получение даты последнего запуска массового погашения
 */
export const fetchLastAllRepaymentDate = async (): Promise<string> => {
  const response = await clientCC.get(`${baseURL}/admin/last-all-repayment-date`);
  return response.data;
};

/**
 * Запуск погашения всех сделок
 */
export const fetchPayOffDebts = async (): Promise<string | null> => {
  try {
    await clientCC.post(`${baseURL}/admin/direct-debits`);
    return null;
  } catch (e: any) {
    return e.response?.data || 'Неизвестная ошибка';
  }
};

/**
 * Отправка запроса в ИБСО на погашение
 */
export const fetchPayment = async (params: PaymentParams): Promise<void> => {
  await clientCC.post(`${baseURL}/admin/create-payment`, {
    creditContractId: params.creditContractId,
    sum: params.sum,
    date: params.date,
  });
};

/**
 * Создание погашения на р/с МКК
 */
export const fetchDirectReceipt = async (params: DirectReceiptParams): Promise<void> => {
  await clientCC.post(`${baseURL}/admin/create-direct-receipt`, {
    creditContractId: params.creditContractId,
    sum: params.sum,
    date: params.date,
    account: params.account,
  });
};

/**
 * Загрузка файла с погашениями через р/с МКК
 */
export const fetchDirectReceipts = async (formData: FormData): Promise<string> => {
  const response = await clientCC.post(`${baseURL}/admin/create-direct-receipts`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

/**
 * Загрузка файла с погашениями
 */
export const fetchUploadManualPayments = async (formData: FormData): Promise<boolean> => {
  const response = await clientCC.post(`${baseURL}/admin/upload-payments-file-dates`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.status === 200;
};

// ============================================
// РЕДАКТИРОВАНИЕ ДАННЫХ КЛИЕНТА
// ============================================

/**
 * Обновление данных клиента
 */
export const fetchUpdateClientData = async (clientData: ClientData): Promise<boolean> => {
  const response = await clientCC.post(`${baseURL}/admin/update-client-data`, {
    id: clientData.id,
    firstName: clientData.firstName,
    lastName: clientData.lastName,
    middleName: clientData.middleName,
    birthDate: clientData.birthDate,
    passportSeries: clientData.passportSeries,
    passportNumber: clientData.passportNumber,
    passportIssueDate: clientData.passportIssueDate,
    passportIssueAuthority: clientData.passportIssueAuthority,
    passportIssueLocation: clientData.passportIssueLocation,
    mobilePhone: clientData.mobilePhone,
    email: clientData.email,
  });
  return response.status === 200;
};

// ============================================
// ЗАГРУЗКА СПРАВОЧНИКОВ
// ============================================

/**
 * Загрузка файла со среднедушевым доходом
 */
export const fetchUploadAverageIncome = async (formData: FormData): Promise<boolean> => {
  const response = await clientCC.post(`${baseURL}/admin/csv/upload-average-income`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.status === 200;
};

/**
 * Загрузка файла со среднерыночным значением ПСК
 */
export const fetchUploadFullCost = async (formData: FormData): Promise<boolean> => {
  const response = await clientCC.post(`${baseURL}/admin/csv/upload-full-cost`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.status === 200;
};

/**
 * Загрузка файла с точками продаж
 */
export const fetchUploadPointOfSale = async (formData: FormData): Promise<boolean> => {
  const response = await clientCC.post(`${baseURL}/admin/csv/upload-points-of-sale`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.status === 200;
};

/**
 * Загрузка файла с меппингом ПСК
 */
export const fetchUploadPskMapping = async (formData: FormData): Promise<boolean> => {
  const response = await clientCC.post(`${baseURL}/admin/csv/upload-psk-mapping`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.status === 200;
};

/**
 * Загрузка реестра ЦБ по 9-ФЗ
 */
export const fetchUpload9FZRegistry = async (formData: FormData): Promise<boolean> => {
  const response = await clientCC.post(`${baseURL}/admin/csv/upload-9fz-registry`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.status === 200;
};

// ============================================
// ДОБАВЛЕНИЕ ПОЛЬЗОВАТЕЛЕЙ
// ============================================

/**
 * Создание нового пользователя с ролью "Менеджер"
 */
export const fetchCreateNewUserManager = async (
  login: string,
  password: string,
  comment: string
): Promise<boolean> => {
  const response = await clientCC.post(`${baseURL}/admin/create-user-role-manager`, {
    login,
    password,
    comment,
  });
  return response.status === 200;
};

// ============================================
// ПРОЧЕЕ
// ============================================

/**
 * Скачивание архива реестров ИБСО
 */
export const fetchDealRegistryPack = async (id: number): Promise<boolean> => {
  const response = await clientCC.post(
    `${baseURL}/admin/deal-registry-pack?creditContractId=${id}`,
    {},
    { responseType: 'blob' }
  );
  forceDownload([response.data], `deal_${id}_registry_pack.zip`);
  return response.status === 200;
};

/**
 * Получение текущего статуса проверки МПЛ 80
 */
export const fetchCurrentMpl80State = async (): Promise<boolean> => {
  const response = await clientCC.get(`${baseURL}/admin/dynamic-properties/fetch-current-mpl-80-state`);
  return response.data;
};

/**
 * Изменение текущего статуса проверки МПЛ 80
 */
export const changeCurrentMpl80State = async (): Promise<boolean> => {
  const response = await clientCC.post(`${baseURL}/admin/dynamic-properties/change-current-mpl-80-state`);
  return response.data;
};

/**
 * Получение текущего статуса проверки МПЛ 50-80
 */
export const fetchCurrentMpl5080State = async (): Promise<boolean> => {
  const response = await clientCC.get(`${baseURL}/admin/dynamic-properties/fetch-current-mpl-50-80-state`);
  return response.data;
};

/**
 * Изменение текущего статуса проверки МПЛ 50-80
 */
export const changeCurrentMpl5080State = async (): Promise<boolean> => {
  const response = await clientCC.post(`${baseURL}/admin/dynamic-properties/change-current-mpl-50-80-state`);
  return response.data;
};

/**
 * Получение текущего статуса автоматической отправки платежей
 */
export const fetchCurrentPaymentState = async (): Promise<boolean> => {
  const response = await clientCC.get(`${baseURL}/admin/dynamic-properties/fetch-current-payment-state`);
  return response.data;
};

/**
 * Изменение текущего статуса автоматической отправки платежей
 */
export const changeCurrentPaymentState = async (): Promise<boolean> => {
  const response = await clientCC.post(`${baseURL}/admin/dynamic-properties/change-current-payment-state`);
  return response.data;
};

/**
 * Получение текущего статуса фильтров заявок по ставкам
 */
export const fetchCurrentRateFilterState = async (): Promise<RateFilter[]> => {
  const response = await clientCC.get(`${baseURL}/admin/dynamic-properties/fetch-current-rate-filter-state`);
  return response.data;
};

/**
 * Изменение текущего статуса фильтра заявок
 */
export const changeCurrentRateFilterState = async (rateFilterId: string): Promise<RateFilter | null> => {
  const response = await clientCC.post(
    `${baseURL}/admin/dynamic-properties/change-current-rate-filter-state/${rateFilterId}`
  );
  return response.data;
};

/**
 * Получение доступных типов документов для генерации
 */
export const getAdminDocumentTypes = async (): Promise<DocumentType[]> => {
  const response = await clientCC.get(`${baseURL}/admin/documents/available-types/`);
  return response.data.content;
};

/**
 * Генерация документа по заявке
 */
export const generateDocumentRequest = async (
  params: DocumentGenerationParams
): Promise<boolean> => {
  try {
    const response = await clientCC.post(
      `${baseURL}/admin/documents/generate`,
      {
        applicationId: params.applicationId,
        genDate: params.genDate,
        save: params.tosave,
        send: params.sendToMyMfo,
        type: params.docType,
      },
      { responseType: 'blob' }
    );
    forceDownload([response.data], `${params.docType}_generated_${params.applicationId}.pdf`);
    return response.status === 200;
  } catch (e) {
    return false;
  }
};
